/**
 * HTML to Markdown over a parsed DOM tree. The widget parses with the browser's
 * `DOMParser` and hands over the body; keeping the walk here lets tests drive it with a
 * small stand-in tree.
 */

const TEXT_NODE = 3;
const ELEMENT_NODE = 1;

const RE_WHITESPACE_RUNS = /\s+/g;
const RE_EXCESS_NEWLINES = /\n{3,}/g;
const RE_MULTI_NEWLINE = /\n{2,}/g;
const RE_PIPE = /\|/g;
const RE_CELL_TAG = /^(?:td|th)$/i;
const RE_HEADING_TAG = /^h([1-6])$/;
const RE_TRAILING_NEWLINE = /\n$/;

const SKIP_TAGS = new Set([
  "script",
  "style",
  "noscript",
  "head",
  "meta",
  "link",
  "title",
  "template",
  "iframe",
]);

const BLOCK_TAGS = new Set([
  "p",
  "div",
  "section",
  "article",
  "header",
  "footer",
  "main",
  "nav",
  "aside",
  "figure",
  "figcaption",
  "h1",
  "h2",
  "h3",
  "h4",
  "h5",
  "h6",
  "ul",
  "ol",
  "li",
  "dl",
  "dt",
  "dd",
  "table",
  "thead",
  "tbody",
  "tfoot",
  "tr",
  "blockquote",
  "pre",
  "hr",
  "br",
  "form",
  "fieldset",
]);

interface WalkContext {
  listDepth: number;
}

function isBlockNode(node: Node | null): boolean {
  return (
    node !== null &&
    node.nodeType === ELEMENT_NODE &&
    BLOCK_TAGS.has((node as Element).tagName.toLowerCase())
  );
}

/**
 * Whitespace-only text between two tags is source formatting. Beside a block element
 * (or at the edge of its parent) it separates nothing, so it is dropped rather than
 * left as a blank line of spaces; between two inline elements it stays one space.
 */
function isLayoutWhitespace(child: Node): boolean {
  if (child.nodeType !== TEXT_NODE || (child.textContent ?? "").trim() !== "") {
    return false;
  }
  const { previousSibling, nextSibling } = child;
  return (
    previousSibling === null ||
    nextSibling === null ||
    isBlockNode(previousSibling) ||
    isBlockNode(nextSibling)
  );
}

function walkChildren(node: Node, ctx: WalkContext): string {
  let out = "";
  for (const child of Array.from(node.childNodes)) {
    if (!isLayoutWhitespace(child)) {
      out += walkNode(child, ctx);
    }
  }
  return out;
}

function inlineText(el: Element, ctx: WalkContext): string {
  return walkChildren(el, ctx).replace(RE_WHITESPACE_RUNS, " ").trim();
}

function handleHeading(el: Element, ctx: WalkContext, level: number): string {
  return `\n\n${"#".repeat(level)} ${inlineText(el, ctx)}\n\n`;
}

function handleList(el: Element, ctx: WalkContext, ordered: boolean): string {
  const indent = "  ".repeat(ctx.listDepth);
  const items = Array.from(el.children).filter(
    (child) => child.tagName.toLowerCase() === "li"
  );
  const lines = items.map((li, index) => {
    const marker = ordered ? `${index + 1}.` : "-";
    const content = walkChildren(li, { listDepth: ctx.listDepth + 1 })
      .trim()
      .replace(RE_MULTI_NEWLINE, "\n");
    return `${indent}${marker} ${content}`;
  });
  return `\n\n${lines.join("\n")}\n\n`;
}

function tableRowLine(cells: string[]): string {
  return `| ${cells.join(" | ")} |`;
}

function handleTable(el: Element): string {
  const rows = Array.from(el.querySelectorAll("tr")).map((tr) =>
    Array.from(tr.children)
      .filter((cell) => RE_CELL_TAG.test(cell.tagName))
      .map((cell) =>
        (cell.textContent ?? "")
          .replace(RE_WHITESPACE_RUNS, " ")
          .trim()
          .replace(RE_PIPE, "\\|")
      )
  );
  const filled = rows.filter((cells) => cells.length > 0);
  if (filled.length === 0) {
    return "";
  }
  const header = filled[0];
  const separator = header.map(() => "---");
  const lines = [
    tableRowLine(header),
    tableRowLine(separator),
    ...filled.slice(1).map(tableRowLine),
  ];
  return `\n\n${lines.join("\n")}\n\n`;
}

function handleBlockquote(el: Element, ctx: WalkContext): string {
  const inner = walkChildren(el, ctx).trim().replace(RE_MULTI_NEWLINE, "\n");
  const quoted = inner
    .split("\n")
    .map((line) => `> ${line}`)
    .join("\n");
  return `\n\n${quoted}\n\n`;
}

function handlePre(el: Element): string {
  const text = (el.textContent ?? "").replace(RE_TRAILING_NEWLINE, "");
  return `\n\n\`\`\`\n${text}\n\`\`\`\n\n`;
}

function handleLink(el: Element, ctx: WalkContext): string {
  const href = el.getAttribute("href") ?? "";
  return `[${inlineText(el, ctx)}](${href})`;
}

function handleImage(el: Element): string {
  const alt = el.getAttribute("alt") ?? "";
  const src = el.getAttribute("src") ?? "";
  return `![${alt}](${src})`;
}

type Handler = (el: Element, ctx: WalkContext) => string;

const TAG_HANDLERS: Record<string, Handler> = {
  p: (el, ctx) => `\n\n${walkChildren(el, ctx).trim()}\n\n`,
  br: () => "\n",
  hr: () => "\n\n---\n\n",
  strong: (el, ctx) => `**${inlineText(el, ctx)}**`,
  b: (el, ctx) => `**${inlineText(el, ctx)}**`,
  em: (el, ctx) => `*${inlineText(el, ctx)}*`,
  i: (el, ctx) => `*${inlineText(el, ctx)}*`,
  code: (el) => `\`${(el.textContent ?? "").trim()}\``,
  pre: (el) => handlePre(el),
  a: (el, ctx) => handleLink(el, ctx),
  img: (el) => handleImage(el),
  ul: (el, ctx) => handleList(el, ctx, false),
  ol: (el, ctx) => handleList(el, ctx, true),
  blockquote: (el, ctx) => handleBlockquote(el, ctx),
  table: (el) => handleTable(el),
};

function walkNode(node: Node, ctx: WalkContext): string {
  if (node.nodeType === TEXT_NODE) {
    return (node.textContent ?? "").replace(RE_WHITESPACE_RUNS, " ");
  }
  if (node.nodeType !== ELEMENT_NODE) {
    return "";
  }
  const el = node as Element;
  const tag = el.tagName.toLowerCase();
  if (SKIP_TAGS.has(tag)) {
    return "";
  }
  const headingMatch = RE_HEADING_TAG.exec(tag);
  if (headingMatch !== null) {
    return handleHeading(el, ctx, Number.parseInt(headingMatch[1], 10));
  }
  const handler = TAG_HANDLERS[tag];
  if (handler !== undefined) {
    return handler(el, ctx);
  }
  return walkChildren(el, ctx);
}

/** Markdown for the contents of a parsed document's `<body>`. */
export function convertDomToMarkdown(body: Node): string {
  const raw = walkChildren(body, { listDepth: 0 });
  return raw.replace(RE_EXCESS_NEWLINES, "\n\n").trim();
}
